// src/components/Inventory/MovePalletModal.js
// "Mover paleta" (§4b): elegir paleta + nueva zona/rack de LISTA → Cloud
// Function movePallet reubica TODO lo que está encima de golpe (ITC
// pallet.moved). También permite crear una paleta nueva (createPallet).
// El escaneo por cámara del QR de paleta queda para la estación de escaneo.
import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/firebase-config';
import { t } from '../../lib/i18n';
import { staticLocationLabel } from '../../lib/locations';
import { downloadQrPng } from '../../utils/qrLabel';
import LocationPicker, { isLocationComplete } from './LocationPicker';

const MovePalletModal = ({ cfg, onClose, onMoved }) => {
    const [palletId, setPalletId] = useState('');
    const [newLocation, setNewLocation] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [doneMsg, setDoneMsg] = useState('');
    const [createdCode, setCreatedCode] = useState(''); // paleta recién creada → imprimir su QR

    const pallets = (cfg?.pallets || []).filter((p) => p.status !== 'retired');
    const canMove = palletId && isLocationComplete(newLocation) && !busy;
    const canCreate = isLocationComplete(newLocation) && !busy;

    const move = async () => {
        setError(''); setBusy(true);
        try {
            const fn = httpsCallable(functions, 'movePallet');
            await fn({ palletId, newLocation });
            setDoneMsg(t('pallet.move.done'));
            onMoved && onMoved();
        } catch (e) {
            console.error('movePallet falló:', e);
            setError(e.message || t('pallet.move.error'));
        } finally { setBusy(false); }
    };

    const create = async () => {
        setError(''); setBusy(true);
        try {
            const fn = httpsCallable(functions, 'createPallet');
            const res = await fn({ location: newLocation });
            setCreatedCode(res.data.code);   // license-plate: hay que imprimir su QR físico
            setDoneMsg(`${t('pallet.new.done')} ${res.data.code}`);
            onMoved && onMoved();
        } catch (e) {
            console.error('createPallet falló:', e);
            setError(e.message || t('pallet.move.error'));
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5 space-y-4">
                <h2 className="text-lg font-bold text-gray-800">{t('pallet.move.title')}</h2>

                {doneMsg ? (
                    <div className="text-center space-y-4 py-4">
                        <p className="text-2xl">✓</p>
                        <p className="font-semibold text-green-700">{doneMsg}</p>
                        {createdCode && (
                            // License-plate: imprime el QR para pegarlo en la paleta física.
                            <button
                                onClick={() => downloadQrPng(JSON.stringify({ t: 'pallet', code: createdCode }), createdCode, `PAL_${createdCode}.png`)}
                                className="px-6 py-3 font-bold text-white bg-gray-800 rounded-lg hover:bg-black w-full">
                                🏷 {t('pallet.qr.download')}
                            </button>
                        )}
                        <button onClick={onClose} className="px-6 py-3 font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 w-full">
                            {t('remnant.close')}
                        </button>
                    </div>
                ) : (
                    <>
                        {pallets.length === 0 ? (
                            <p className="text-sm text-gray-600 bg-gray-50 rounded-md p-3">{t('pallet.move.firstPallet')}</p>
                        ) : (
                            <div>
                                <label className="block text-sm font-medium text-gray-700">{t('pallet.move.which')}</label>
                                <select className="mt-1 w-full p-2 border border-gray-300 rounded-md" value={palletId}
                                    onChange={(e) => setPalletId(e.target.value)}>
                                    <option value="">{t('pallet.move.pick')}</option>
                                    {pallets.map((p) => (
                                        <option key={p.id} value={p.id}>
                                            {p.code || p.id} — {staticLocationLabel(p.currentLocation, cfg) || '—'}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div>
                            <p className="block text-sm font-medium text-gray-700 mb-1">{t('pallet.move.to')}</p>
                            {/* fixedOnly: una paleta solo vive en rack/zona, nunca en otra paleta */}
                            <LocationPicker cfg={cfg} value={newLocation} onChange={setNewLocation} fixedOnly />
                        </div>

                        {error && <p className="text-sm text-red-600">{error}</p>}

                        {/* Mover una paleta existente (solo si hay paletas). */}
                        {pallets.length > 0 && (
                            <button onClick={move} disabled={!canMove}
                                className="w-full px-4 py-3 font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40">
                                {busy ? t('pallet.move.moving') : t('pallet.move.save')}
                            </button>
                        )}

                        {/* Crear paleta nueva: el destino de arriba es DÓNDE nace la paleta. */}
                        <button onClick={create} disabled={!canCreate} title={t('pallet.new.prompt')}
                            className="w-full px-4 py-3 text-sm font-bold text-green-700 bg-green-50 border border-green-300 rounded-lg hover:bg-green-100 disabled:opacity-40 disabled:cursor-not-allowed">
                            + {t('pallet.new')}
                        </button>
                        {!canCreate && <p className="text-xs text-gray-500 text-center">{t('pallet.new.hint')}</p>}

                        <button onClick={onClose} className="w-full px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800">
                            {t('remnant.cancel')}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default MovePalletModal;
